import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { Skeleton } from '../../../components/ui/skeleton';
import { Wand2, Loader2, Trash2 } from 'lucide-react';
import CreativePreviewCard from './CreativePreviewCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HistoryGallery() {
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMode, setFilterMode] = useState('all');

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/creative/history`);
      // Backend returns {"history": items}, map to camelCase fields for frontend
      const historyItems = (response.data.history || [])
        // Filter out failed items and items without imageUrl
        .filter(item => item.status !== 'failed' && (item.output_image_url || item.batch_output_urls))
        .map(item => ({
          imageUrl: item.output_image_url,
          qualityTier: item.quality_tier,
          createdAt: item.created_at,
          mode: item.mode,
          jobId: item.job_id,
          status: item.status,
          batchOutputUrls: item.batch_output_urls,
          costCents: item.cost_cents,
        }));
      setCreatives(historyItems);
    } catch (error) {
      console.error('Load history error:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCreative = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this creative? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/creative/job/${jobId}`);
      // Remove from local state
      setCreatives(creatives.filter(c => c.jobId !== jobId));
      toast.success('Creative deleted');
    } catch (error) {
      console.error('Delete creative error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete creative');
    }
  };

  const filteredCreatives = filterMode === 'all'
    ? creatives
    : creatives.filter(c => c.mode === filterMode);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="card overflow-hidden">
              <CardContent className="p-0">
                <Skeleton className="w-full aspect-square bg-white/5" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-4 w-20 bg-white/5" />
                  <Skeleton className="h-3 w-40 bg-white/5" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={() => setFilterMode('all')}
          variant={filterMode === 'all' ? 'default' : 'outline'}
          className={filterMode === 'all' ? 'bg-[#00d4aa] hover:bg-[#00e6bb] text-black' : 'border-white/10 text-white'}
        >
          All
        </Button>
        <Button
          onClick={() => setFilterMode('branded')}
          variant={filterMode === 'branded' ? 'default' : 'outline'}
          className={filterMode === 'branded' ? 'bg-[#00d4aa] hover:bg-[#00e6bb] text-black' : 'border-white/10 text-white'}
        >
          Branded
        </Button>
        <Button
          onClick={() => setFilterMode('fresh')}
          variant={filterMode === 'fresh' ? 'default' : 'outline'}
          className={filterMode === 'fresh' ? 'bg-[#00d4aa] hover:bg-[#00e6bb] text-black' : 'border-white/10 text-white'}
        >
          Fresh
        </Button>
      </div>

      {/* Gallery */}
      {filteredCreatives.length === 0 ? (
        <Card className="card">
          <CardContent className="p-12 text-center">
            <Wand2 className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Creatives Yet</h3>
            <p className="text-white/60">
              Your generated creatives will appear here. Start by creating a {filterMode === 'all' ? 'branded or fresh' : filterMode} creative.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCreatives.map((creative, idx) => (
            <div key={idx} className="relative group">
              <CreativePreviewCard
                imageUrl={creative.imageUrl}
                mode={creative.mode}
                tier={creative.qualityTier}
                createdAt={creative.createdAt}
              />
              {/* Delete Button */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDeleteCreative(creative.jobId)}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500/20 hover:bg-red-500/30 text-red-400"
                title="Delete this creative"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default HistoryGallery;
